import { useCallback, useEffect, useMemo, useState } from "react";

import type { ActorAttribution, ChannelMessage } from "../blackboardModels";

import {
  readRetainedChannelHistoryAsUser,
  resolveActorAttributionsAsUser,
} from "../../../dashboard-client";

interface LegacyHistoryResponse {
  hasMore: boolean;
  messages: ChannelMessage[];
}

type HistoryMode = "channel" | "legacy";

export function useChannelMessages(
  channelId: string | null,
  proxyInstanceId: string | null,
  liveMessages: ChannelMessage[],
  knownActorAttributions: ActorAttribution[],
): {
  actorAttributions: ActorAttribution[];
  appendLocalUserMessage: (message: ChannelMessage) => void;
  hasMore: boolean;
  historyError: string | null;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
  messages: ChannelMessage[];
} {
  const [historicalMessages, setHistoricalMessages] = useState<
    ChannelMessage[]
  >([]);
  const [actorAttributions, setActorAttributions] = useState<
    ActorAttribution[]
  >([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [mode, setMode] = useState<HistoryMode>("channel");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [localUserMessages, setLocalUserMessages] = useState<ChannelMessage[]>(
    [],
  );

  useEffect(() => {
    setHistoricalMessages([]);
    setActorAttributions([]);
    setLocalUserMessages([]);
    setHistoryError(null);
    setMode("channel");
    setNextCursor(null);
    setHasMore(false);
    if (!channelId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await readRetainedChannelHistoryAsUser({
          path: { channelId },
          throwOnError: false,
        });
        if (response.data) {
          const { data } = response;
          if (!cancelled) {
            setHistoricalMessages(sortMessages(data.messages));
            setActorAttributions(data.actorAttributions);
            setNextCursor(data.nextCursor);
            setHasMore(data.nextCursor !== null);
          }
          return;
        }
        if (
          !shouldFallbackToLegacyHistory(
            response.response.status,
            proxyInstanceId,
          )
        ) {
          throw new Error(
            `History request failed with HTTP ${response.response.status}`,
          );
        }
        const legacyResponse = await fetch(
          `/user/instances/${proxyInstanceId}/channels/${channelId}/messages`,
          { credentials: "include" },
        );
        if (!legacyResponse.ok) {
          throw new Error(
            `History request failed with HTTP ${legacyResponse.status}`,
          );
        }
        const data: LegacyHistoryResponse = await legacyResponse.json();
        if (!cancelled) {
          const messages = sortMessages(data.messages);
          setMode("legacy");
          setHistoricalMessages(messages);
          setNextCursor(messages[0]?.id ?? null);
          setHasMore(data.hasMore);
        }
      } catch (error) {
        if (!cancelled) {
          setHistoryError(
            error instanceof Error
              ? error.message
              : "Channel history is unavailable.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [channelId, proxyInstanceId]);

  const messages = useMemo(() => {
    const seen = new Set<string>();
    const merged: ChannelMessage[] = [];
    for (const list of [historicalMessages, liveMessages, localUserMessages]) {
      for (const message of list) {
        if (message.channelId !== channelId || seen.has(message.id)) {
          continue;
        }
        seen.add(message.id);
        merged.push(message);
      }
    }
    return sortMessages(merged);
  }, [historicalMessages, liveMessages, localUserMessages, channelId]);

  useEffect(() => {
    if (!channelId) {
      return;
    }
    const knownIds = new Set([
      ...knownActorAttributions.map((attribution) => attribution.id),
      ...actorAttributions.map((attribution) => attribution.id),
    ]);
    const unresolvedIds = [
      ...new Set(
        messages
          .filter((message) => message.senderType === "instance")
          .map((message) => message.senderId),
      ),
    ]
      .filter((id) => !knownIds.has(id))
      .slice(0, 100);
    if (unresolvedIds.length === 0) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await resolveActorAttributionsAsUser({
          body: { ids: unresolvedIds },
        });
        if (!cancelled) {
          setActorAttributions((previous) =>
            mergeActorAttributions(previous, response.data.actorAttributions),
          );
        }
      } catch {
        // Message rendering already has senderDisplayName/id fallbacks.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [actorAttributions, channelId, knownActorAttributions, messages]);

  const loadMore = useCallback(async () => {
    if (
      !channelId ||
      nextCursor === null ||
      loadingMore ||
      !hasMore ||
      (mode === "legacy" && proxyInstanceId === null)
    ) {
      return;
    }
    setLoadingMore(true);
    try {
      if (mode === "channel") {
        const { data } = await readRetainedChannelHistoryAsUser({
          path: { channelId },
          query: { cursor: nextCursor },
        });
        setHistoricalMessages((previous) =>
          mergeHistoricalMessages(data.messages, previous),
        );
        setActorAttributions((previous) =>
          mergeActorAttributions(previous, data.actorAttributions),
        );
        setNextCursor(data.nextCursor);
        setHasMore(data.nextCursor !== null);
      } else {
        const response = await fetch(
          `/user/instances/${proxyInstanceId}/channels/${channelId}/messages?before=${encodeURIComponent(nextCursor)}`,
          { credentials: "include" },
        );
        if (!response.ok) {
          throw new Error(
            `History request failed with HTTP ${response.status}`,
          );
        }
        const data: LegacyHistoryResponse = await response.json();
        setHistoricalMessages((previous) =>
          mergeHistoricalMessages(data.messages, previous),
        );
        setNextCursor(data.messages[0]?.id ?? null);
        setHasMore(data.hasMore);
      }
    } catch (error) {
      setHistoryError(
        error instanceof Error
          ? error.message
          : "Channel history is unavailable.",
      );
    } finally {
      setLoadingMore(false);
    }
  }, [channelId, hasMore, loadingMore, mode, nextCursor, proxyInstanceId]);

  const appendLocalUserMessage = useCallback((message: ChannelMessage) => {
    setLocalUserMessages((previous) => [...previous, message]);
  }, []);

  return {
    actorAttributions,
    appendLocalUserMessage,
    hasMore,
    historyError,
    loadMore,
    loadingMore,
    messages,
  };
}

function mergeHistoricalMessages(
  older: ChannelMessage[],
  existing: ChannelMessage[],
): ChannelMessage[] {
  const seen = new Set(existing.map((message) => message.id));
  return sortMessages([
    ...older.filter((message) => !seen.has(message.id)),
    ...existing,
  ]);
}

function mergeActorAttributions(
  existing: ActorAttribution[],
  incoming: ActorAttribution[],
): ActorAttribution[] {
  const byId = new Map(
    existing.map((attribution) => [attribution.id, attribution]),
  );
  for (const attribution of incoming) {
    byId.set(attribution.id, attribution);
  }
  return [...byId.values()];
}

function sortMessages(messages: ChannelMessage[]): ChannelMessage[] {
  return messages.toSorted((left, right) => {
    const timestampDelta =
      new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
    return timestampDelta === 0
      ? left.id.localeCompare(right.id)
      : timestampDelta;
  });
}

function shouldFallbackToLegacyHistory(
  status: number,
  proxyInstanceId: string | null,
): boolean {
  return proxyInstanceId !== null && (status === 404 || status === 501);
}

export const __testing = {
  mergeActorAttributions,
  mergeHistoricalMessages,
  shouldFallbackToLegacyHistory,
  sortMessages,
};
